// Print-export utilities: device-limit probing, DPI-targeted workspace
// export as a Blob, and remote-asset flattening for offline-safe saves.
import { config } from 'fabric';
import { registerBaker, getBaker } from './emboss/bakeRegistry.js';
import { getRenderCaps } from './renderCaps.js';
import {
  fxBeginExport,
  fxEndExport,
  fxPrimeExport,
  fxPrimeAssets,
  fxCleanupExport,
} from './fx/fxRender.js';

// No-silent-caps: once-per-key console notes for print-resolution shortfalls.
const printWarned = new Set();
function warnPrintOnce(key, msg) {
  if (printWarned.has(key)) return;
  printWarned.add(key);
  console.warn(`[export] ${msg}`);
}

// ---- device max-canvas probe (one-shot, cached) ----
// Above the GPU/canvas ceiling, toDataURL/toCanvasElement silently produce
// blank output — clamp the export multiplier instead.
let maxSidePromise = null;
function canvasSideWorks(side) {
  try {
    const c = document.createElement('canvas');
    c.width = side;
    c.height = 1;
    const ctx = c.getContext('2d');
    if (!ctx) return false;
    ctx.fillStyle = '#fff';
    ctx.fillRect(side - 1, 0, 1, 1);
    return ctx.getImageData(side - 1, 0, 1, 1).data[3] !== 0;
  } catch (_) {
    return false;
  }
}

export function probeMaxCanvasSide() {
  if (!maxSidePromise) {
    maxSidePromise = Promise.resolve().then(() => {
      for (const side of [16384, 11180, 8192, 4096]) {
        if (canvasSideWorks(side)) return side;
      }
      return 2048;
    });
  }
  return maxSidePromise;
}

// ---- recipe-image print-DPI pre-pass ----
// Baked recipe images (text3d, emboss-text) serialize at a modest standing
// long edge (the design ground truth); a poster-size print needs more. Before
// rasterizing, re-bake every recipe object whose needed pixels (displayed px ×
// multiplier) exceed its standing bake, swap the image source for the render,
// and RESTORE the original src afterward — the caller MUST run the returned
// restore() in a `finally` (risk: a big print bake must never leak into
// history/autosave). WebGL/bake failures skip silently: the standing bake
// exports as-is.
//
// The tool→bake map lives HERE (not in bakeRegistry) so the registry stays a
// plain, boot-safe module: each `bake` is a LAZY wrapper, so text3d's three.js
// and emboss's fabric StaticCanvas load only when a print bake actually runs.
let bakersRegistered = false;
function ensureBakersRegistered() {
  if (bakersRegistered) return;
  bakersRegistered = true;
  const caps = getRenderCaps();
  // Both bakers now run to the RUNTIME export ceiling (print300 lane,
  // 2026-07-17): text3dRecipe's old internal 4096 clamp is runtime-read and
  // renderer3d TILES the GL work past its render-target cap; embossRecipe
  // (pure 2D) raises fabric's cache limits transiently and area-guards
  // internally. 16384-class on Chromium — the 44×66in@300dpi long-edge need
  // (13,200px) is inside the budget. Standing bakes stay ≤ BAKE_STAND_CAPS
  // (the re-bake-on-scale hook clamps independently — JSON budget).
  const printBakeCeiling = Math.min(caps.exportSide, caps.canvasMaxSide || caps.exportSide);
  registerBaker('text3d', {
    maxPx: printBakeCeiling,
    bake: async (params, opts) => {
      const { bakeText3d } = await import('./text3d/text3dRecipe.js');
      return bakeText3d(params, opts);
    },
  });
  registerBaker('emboss-text', {
    maxPx: printBakeCeiling,
    bake: async (params, opts) => {
      const { bakeEmboss } = await import('./emboss/embossRecipe.js');
      return bakeEmboss(params, opts);
    },
  });
}

/** The lazy baker map, shared with useCanvasEditor's re-bake-on-scale hook
 * (standing-bake upgrades ride the same bake entry points as the print
 * pre-pass — one policy, one code path). */
export function bakerFor(tool) {
  ensureBakersRegistered();
  return getBaker(tool) || null;
}

async function upscaleRegisteredBakes(canvas, multiplier) {
  const swapped = [];
  const restore = async () => {
    for (const s of swapped) {
      try {
        await s.object.setSrc(s.src);
        s.object.set({ scaleX: s.scaleX, scaleY: s.scaleY, dirty: true });
        s.object.setCoords();
      } catch (_) { /* keep going — restore every object we can */ }
    }
    if (swapped.length) canvas.requestRenderAll();
  };

  ensureBakersRegistered();
  const targets = canvas.getObjects().filter((o) =>
    o?.data?.tool && getBaker(o.data.tool) && o.data.params &&
    typeof o.getSrc === 'function' && o.visible !== false);
  if (!targets.length) return restore;

  for (const object of targets) {
    const baker = getBaker(object.data.tool);
    if (!baker) continue;
    try {
      // Need = placed size × the print multiplier. The workspace is
      // 72dpi-based, so at targetDPI 300 this IS inches_placed × 300 — the
      // 300dpi contract's formula, capped only by the runtime ceilings.
      const neededPx = Math.max(object.getScaledWidth(), object.getScaledHeight()) * multiplier;
      const standingPx = Number(object.data.bakePx) || 2048;
      if (!(neededPx > standingPx * 0.9)) continue;
      const targetPx = Math.min(Math.ceil(neededPx), baker.maxPx);
      if (neededPx > targetPx * 1.05) {
        // Fires only past the RUNTIME export ceiling now (print300 lane):
        // a baked-recipe object needing more than ~16384px is >54.6in of
        // solid recipe art at 300dpi — documented cap, honest shortfall.
        warnPrintOnce(`bake-short:${object.data.tool}:${Math.round(neededPx / 256)}`,
          `${object.data.tool} print bake capped: need ${Math.round(neededPx)}px long edge, `
          + `delivering ${targetPx}px (baker cap ${baker.maxPx}). PRINT300_TODO: an object `
          + `needing >${baker.maxPx}px (>${Math.round((baker.maxPx / 300) * 10) / 10}in at `
          + `300dpi) delivers the cap; per-strip re-bakes would be the next step.`);
      }
      if (targetPx <= standingPx) continue;

      // Lazy-imports the recipe module and rasterizes at print resolution.
      const { dataUrl } = await baker.bake(object.data.params, { targetPx });
      const entry = {
        object,
        src: object.getSrc(),
        scaleX: object.scaleX,
        scaleY: object.scaleY,
      };
      const displayedW = object.getScaledWidth();
      const displayedH = object.getScaledHeight();
      await object.setSrc(dataUrl);
      swapped.push(entry); // only after the swap actually happened
      // setSrc resets width/height to the new naturals — keep the displayed
      // size identical so the print transform is untouched.
      object.set({
        scaleX: displayedW / object.width,
        scaleY: displayedH / object.height,
        dirty: true,
      });
      object.setCoords();
    } catch (_) {
      // No WebGL / context loss / bake error: this object prints its standing bake.
    }
  }
  return restore;
}

// ---- live-fx source-step print pre-pass ----
// kind:'source' steps (live 3D extrude) render through WebGL, which must
// NEVER run synchronously inside toCanvasElement's render. Before the render
// pass, bake every such object at the export multiplier (long edge capped at
// 4096 inside the effect) and prime its __fxExportStash; the fx seam draws
// that stash during the export render. The returned cleanup MUST run in the
// caller's finally — it frees the oversized print bakes so they never
// outlive the export. Failures are silent per object: it exports flat, the
// same posture as the screen path.
// ---- single-shot bake law (Depth-Everywhere lane F4, 2026-07-22) ----
// CONCEPT-3d-letter-volumes.md, "print honesty": transmission/gummy materials
// render through a FULL-VIEWPORT render target and therefore CANNOT be tiled
// without seams. Such a preset must bake SINGLE-SHOT (one SSAA surface). The
// export path carries a `bakePosture: 'tiled' | 'singleshot'` request; today
// every export stays 'tiled' (no behavior change). The honest guard below wires
// the V2 degrade: an fx object whose material DECLARES itself single-shot-
// required (a step or its params flagged `singleshotRequired: true` — the
// contract F3's gummy preset will set) is checked against the single-shot GL
// ceiling; if the poster needs more pixels than one GL surface can deliver, the
// object DEGRADES to its floor material (matcap-faux) with a console.info, so a
// too-big transmission bake never silently ships tiled/seamed pixels. Nothing
// sets the flag yet, so the check is inert — but it is WIRED, not deferred.
function fxSingleshotRequired(object) {
  const fx = object && object.data && object.data.fx;
  if (!Array.isArray(fx)) return false;
  return fx.some((step) => step && (step.singleshotRequired === true
    || (step.params && step.params.singleshotRequired === true)));
}

async function primeFxSourceBakes(canvas, multiplier, { bakePosture = 'tiled' } = {}) {
  const t0 = performance.now();
  const targets = [];
  const walk = (objs) => objs.forEach((o) => {
    if (typeof o.getObjects === 'function') walk(o.getObjects());
    if (Array.isArray(o.data?.fx) && o.data.fx.length && o.visible !== false) targets.push(o);
  });
  walk(canvas.getObjects());

  // Single-shot degrade guard. Only objects whose material declares itself
  // single-shot-required are ever checked; getMaxRenderSide (which creates the
  // GL context) is lazy-imported ONLY then — a normal export never touches it,
  // keeping this fully inert today.
  const degraded = [];
  const singleshotTargets = targets.filter(fxSingleshotRequired);
  if (singleshotTargets.length) {
    let singleShotLimit = 0;
    try {
      const { getMaxRenderSide } = await import('./text3d/renderer3d.js');
      singleShotLimit = getMaxRenderSide();
    } catch (_) { singleShotLimit = 0; }
    for (const object of singleshotTargets) {
      const needPx = Math.max(
        object.getScaledWidth ? object.getScaledWidth() : 0,
        object.getScaledHeight ? object.getScaledHeight() : 0,
      ) * multiplier;
      if (needPx > singleShotLimit) {
        object.__fxSingleshotDegraded = true; // downstream material builder honors this → floor material
        degraded.push(object);
        console.info(`[export] single-shot bake law: an object needs ${Math.round(needPx)}px on a side but `
          + `the single-shot GL ceiling is ${singleShotLimit}px — degrading it to its FLOOR material `
          + `(matcap-faux) for this export. A tiled bake of a single-shot-required (e.g. transmission) `
          + `material would seam. (bakePosture=${bakePosture})`);
      }
    }
  }
  // Filter-step ASSET warm-up (cold-export texture lane): a filter step's
  // applySync (a sticker plate's textured fill) paints a neutral PLACEHOLDER
  // while its tile decodes — invisible on screen (the async upgrade repaints)
  // but a cold export renders synchronously and IS the deliverable. Kick every
  // filter step's decode off NOW (overlaps the source bakes below) and await
  // them before returning, so the export render paints the real texture, not
  // the placeholder. fxPrimeAssets is a no-op (0) for solid/assetless stacks.
  const assetPrimes = targets.map((o) => fxPrimeAssets(o));
  let primed = 0;
  for (const object of targets) {
    try {
      // THE NEED LAW (print300): need = placed size × print multiplier —
      // document truth only. getObjectScaling (own × ancestor groups) is the
      // right term; getTotalObjectScaling would leak the SCREEN zoom/retina
      // into the print bake (a fitted poster view at zoom 0.2 under-baked
      // every export prime 5× before this fix).
      const scl = (typeof object.getObjectScaling === 'function'
        ? object.getObjectScaling()
        : null) || { x: object.scaleX || 1, y: object.scaleY || 1 };
      const pixelScale = Math.max(scl.x || 1, scl.y || 1) * multiplier;
      if (await fxPrimeExport(object, pixelScale)) primed += 1;
    } catch (_) { /* this object exports flat */ }
  }
  // Await the filter-asset decodes kicked off above (overlapped the source
  // bakes). A failed/absent asset falls back to its placeholder — screen
  // posture — never blocking the export.
  let assetsPrimed = 0;
  try {
    const counts = await Promise.all(assetPrimes);
    assetsPrimed = counts.reduce((n, c) => n + (c || 0), 0);
  } catch (_) { /* placeholder fallback */ }
  return {
    cleanup: () => {
      targets.forEach((o) => fxCleanupExport(o));
      // The degrade flag is transient — it must never outlive the export bake
      // (same law as the print-resolution bake swaps: nothing leaks to history).
      degraded.forEach((o) => { delete o.__fxSingleshotDegraded; });
    },
    ms: Math.round(performance.now() - t0),
    primed,
    assetsPrimed,
    bakePosture,
    singleshotDegraded: degraded.length,
  };
}

// ---- strip-mode internals (print300 lane, 2026-07-17) ----
// A 44×66in poster at 300dpi is 13,200×19,800 px = 261.4Mpx ≈ 997MB RGBA —
// past ANY single canvas surface. Strip mode renders the poster in
// horizontal strips (each within the transient export budgets), streams the
// rows through the pngStream encoder (fflate zlib — see lib/pngStream.js
// for the dependency note), and delivers the SAME Blob contract as the
// single-surface path with the byte dimensions EXACTLY inches×DPI.

/** True when the canvas clipPath is a plain axis-aligned rect that fully
 * covers the export region — the workspace-clone clip in practice. Then
 * destination-in with it is the identity over every strip pixel, and strip
 * mode can skip it: at 261Mpx the mask's own cache would be downscaled and
 * resampled (soft edge pixels), while skipping it is EXACT. */
function clipCoversRegion(clip, { left, top, width, height }) {
  try {
    if (!clip) return true;
    if (String(clip.type || '').toLowerCase() !== 'rect') return false;
    if (clip.rx || clip.ry || clip.angle || clip.skewX || clip.skewY) return false;
    if (clip.inverted || clip.absolutePositioned) return false;
    if (clip.originX !== 'left' || clip.originY !== 'top') return false;
    const eps = 0.5;
    const cw = (clip.width || 0) * (clip.scaleX || 1);
    const ch = (clip.height || 0) * (clip.scaleY || 1);
    return clip.left <= left + eps && clip.top <= top + eps
      && clip.left + cw >= left + width - eps
      && clip.top + ch >= top + height - eps;
  } catch (_) {
    return false;
  }
}

/**
 * Disable fabric object caching on every object whose cache AT the print
 * multiplier would blow the export budgets — fabric would silently render
 * a downscaled cache (soft pixels) otherwise. Direct rendering per strip is
 * exact: every strip draws with the same multiplier, so rasterization is
 * identical across strips and joins stay continuous. Walks nested groups
 * (a full-bleed group's cache is just as oversized as a full-bleed rect's).
 * Returns the restore fn (call in finally).
 */
function disableOversizedCaches(canvas, multiplier, caps) {
  const disabled = [];
  const sideCap = caps.exportSide * 0.98;
  const areaCap = caps.exportArea * 0.98;
  const visit = (objs) => {
    for (const o of objs || []) {
      if (!o) continue;
      let w = 0;
      let h = 0;
      try {
        const s = typeof o.getObjectScaling === 'function'
          ? o.getObjectScaling()
          : { x: o.scaleX || 1, y: o.scaleY || 1 };
        w = (o.width || 0) * Math.abs(s.x || 1) * multiplier;
        h = (o.height || 0) * Math.abs(s.y || 1) * multiplier;
      } catch (_) { /* unmeasurable — leave its caching alone */ }
      if (w > sideCap || h > sideCap || w * h > areaCap) {
        disabled.push({ o, prev: o.objectCaching });
        o.objectCaching = false;
        o.dirty = true;
      }
      if (typeof o.getObjects === 'function') visit(o.getObjects());
    }
  };
  visit(canvas.getObjects());
  return {
    count: disabled.length,
    restore: () => {
      for (const { o, prev } of disabled) {
        o.objectCaching = prev;
        o.dirty = true;
      }
    },
  };
}

/**
 * toCanvasElement-equivalent render of ONE device-pixel region: same
 * multiplier every call, viewportTransform translated by the region's
 * integer pixel offset — identical rasterization per strip, which is the
 * whole seam-continuity argument (a drawImage/fillText split across two
 * strips samples identical source coords in both). Mirrors fabric 6.9.1's
 * toCanvasElement bracket exactly (direct width/height swap + renderCanvas),
 * minus the canvas.clipPath (handled by the caller — see clipCoversRegion).
 */
function renderRegionToCanvas(canvas, { multiplier, left, top, xPx, yPx, wPx, hPx }) {
  const el = document.createElement('canvas');
  el.width = wPx;
  el.height = hPx;
  // Strip pixels are read back chunk-by-chunk right after the render.
  const ctx = el.getContext('2d', { willReadFrequently: true });
  const prevVpt = canvas.viewportTransform;
  const prevW = canvas.width;
  const prevH = canvas.height;
  const prevRetina = canvas.enableRetinaScaling;
  const prevSkipControls = canvas.skipControlsDrawing;
  canvas.enableRetinaScaling = false;
  canvas.viewportTransform = [
    multiplier, 0, 0, multiplier,
    -left * multiplier - xPx,
    -top * multiplier - yPx,
  ];
  canvas.width = wPx;
  canvas.height = hPx;
  canvas.skipControlsDrawing = true;
  canvas.calcViewportBoundaries();
  try {
    canvas.renderCanvas(ctx, canvas._objects);
  } finally {
    canvas.viewportTransform = prevVpt;
    canvas.width = prevW;
    canvas.height = prevH;
    canvas.calcViewportBoundaries();
    canvas.enableRetinaScaling = prevRetina;
    canvas.skipControlsDrawing = prevSkipControls;
  }
  return el;
}

/** Strip/column geometry for a target raster under the export budgets.
 * Strip heights are a multiple of 8 (clean joins/chunking); columns appear
 * only when the target WIDTH itself exceeds a single canvas side (a
 * landscape 66×44 — rows are then stitched from column tiles). */
function stripPlanFor(targetW, targetH, caps) {
  const colCap = Math.min(caps.exportSide, caps.canvasMaxSide || caps.exportSide);
  let cols = Math.max(1, Math.ceil(targetW / colCap));
  let colW = Math.min(colCap, Math.ceil(targetW / cols / 8) * 8);
  while (cols > 1 && colW * (cols - 1) >= targetW) cols -= 1; // degenerate last col
  const colWidths = [];
  for (let k = 0; k < cols; k++) {
    colWidths.push(k < cols - 1 ? colW : targetW - colW * (cols - 1));
  }
  const widest = Math.max(...colWidths);
  const stripH = Math.max(8, Math.min(
    Math.floor(caps.exportSide / 8) * 8,
    Math.floor(caps.exportArea / widest / 8) * 8,
  ));
  const strips = Math.ceil(targetH / stripH);
  return { cols, colWidths, stripH, strips };
}

/** Render + stream-encode the strip plan. Runs INSIDE the export bracket
 * (config raised, fxBeginExport, primes done). Returns the Blob + metrics. */
async function renderStripsToPng(canvas, region, opts) {
  const { left, top, width, height } = region;
  const { multiplier, targetW, targetH, targetDPI, caps, pngFilter, pngLevel, onProgress } = opts;
  const { createPngStream } = await import('./pngStream.js');
  const plan = stripPlanFor(targetW, targetH, caps);
  const enc = createPngStream({
    width: targetW,
    height: targetH,
    filter: pngFilter === 0 ? 0 : 2,
    level: Number.isFinite(pngLevel) ? pngLevel : 4,
    dpi: targetDPI,
  });

  const cachesOff = disableOversizedCaches(canvas, multiplier, caps);
  const prevSkipOffscreen = canvas.skipOffscreen;
  // An object fully OUTSIDE a strip can still spill shadows/fx into it —
  // isOnScreen culling knows nothing about spill, so it is off for strips.
  canvas.skipOffscreen = false;
  const prevClip = canvas.clipPath;
  const clipElided = clipCoversRegion(prevClip, region);
  if (clipElided && prevClip) canvas.clipPath = undefined;
  else if (prevClip) {
    console.info('[export] strip mode: canvas.clipPath is not a full-coverage rect — '
      + 'keeping it (its mask may resample at poster scale).');
  }

  let renderMs = 0;
  let encodeMs = 0;
  let peakHeap = 0;
  const sampleHeap = () => {
    try {
      const u = performance.memory && performance.memory.usedJSHeapSize;
      if (u > peakHeap) peakHeap = u;
    } catch (_) { /* non-Chromium */ }
  };
  try {
    const rowChunk = Math.max(16, Math.min(512, Math.floor((4 * 1024 * 1024) / targetW)));
    let rowBuf = plan.cols > 1 ? new Uint8Array(targetW * 4) : null;
    for (let s = 0; s < plan.strips; s++) {
      const yPx = s * plan.stripH;
      const hPx = Math.min(plan.stripH, targetH - yPx);
      const t0 = performance.now();
      const tiles = [];
      let xPx = 0;
      for (let k = 0; k < plan.cols; k++) {
        const wPx = plan.colWidths[k];
        tiles.push(renderRegionToCanvas(canvas, { multiplier, left, top, xPx, yPx, wPx, hPx }));
        xPx += wPx;
      }
      const t1 = performance.now();
      renderMs += t1 - t0;
      sampleHeap();
      // Feed rows in modest chunks so ImageData stays small (the strip
      // canvas itself is the big allocation; readbacks are ~16MB each).
      for (let y0 = 0; y0 < hPx; y0 += rowChunk) {
        const n = Math.min(rowChunk, hPx - y0);
        if (plan.cols === 1) {
          const img = tiles[0].getContext('2d').getImageData(0, y0, targetW, n);
          enc.pushRows(img.data, n);
        } else {
          // Stitch each row across the column tiles, one row at a time.
          const imgs = tiles.map((t, k) => t.getContext('2d')
            .getImageData(0, y0, plan.colWidths[k], n));
          for (let r = 0; r < n; r++) {
            let off = 0;
            for (let k = 0; k < plan.cols; k++) {
              const wB = plan.colWidths[k] * 4;
              rowBuf.set(imgs[k].data.subarray(r * wB, (r + 1) * wB), off);
              off += wB;
            }
            enc.pushRows(rowBuf, 1);
          }
        }
      }
      encodeMs += performance.now() - t1;
      // Free the strip backing store NOW — the next strip re-allocates.
      for (const t of tiles) {
        t.width = 1;
        t.height = 1;
      }
      tiles.length = 0;
      sampleHeap();
      console.info(`[export] strip ${s + 1}/${plan.strips} done `
        + `(${targetW}×${hPx}px, render ${Math.round(t1 - t0)}ms)`);
      if (typeof onProgress === 'function') {
        try {
          onProgress({ phase: 'strip', strip: s + 1, strips: plan.strips });
        } catch (_) { /* progress is advisory */ }
      }
    }
    rowBuf = null;
    const tF = performance.now();
    const blob = enc.finish();
    encodeMs += performance.now() - tF;
    sampleHeap();
    return {
      blob,
      strips: plan.strips,
      stripHeight: plan.stripH,
      cols: plan.cols,
      renderMs: Math.round(renderMs),
      encodeMs: Math.round(encodeMs),
      peakHeapMB: peakHeap ? Math.round(peakHeap / 1048576) : null,
    };
  } finally {
    if (prevClip) canvas.clipPath = prevClip;
    canvas.skipOffscreen = prevSkipOffscreen;
    cachesOff.restore();
  }
}

// ---- DPI-targeted export ----
// Renders the workspace at targetDPI via toCanvasElement (applies the
// workspace clipPath — verified v5.5.2) → PNG Blob when the raster fits a
// single surface; larger documents (the 300-DPI law: EVERY export renders
// at inches×300, posters to 44×66in) switch to STRIP MODE — horizontal
// strips + a streamed PNG assembly (renderStripsToPng above) — and deliver
// the exact same Blob contract at the exact target pixel size. No giant
// base64 strings; PrintPreview consumes a blob: URL directly (it fetch()es
// and also keeps it as <img src> — do NOT revoke the URL while PrintPreview
// is alive; we revoke the PREVIOUS handoff).
//
// opts (all additive — callers unchanged):
//   pngFilter   0|2                    strip-mode PNG scanline filter (default 2/Up)
//   pngLevel    0-9                    strip-mode deflate level (default 4)
//   onProgress  fn                     coarse per-strip callback {phase, strip, strips}
//   bakePosture 'tiled'|'singleshot'   3D-export bake law (default 'tiled'); a
//                                      single-shot-required material degrades to
//                                      its floor material if the poster exceeds
//                                      single-shot GL limits (F4 wiring — inert
//                                      until V2 gummy/transmission sets the flag).
export async function exportWorkspaceBlob(canvas, workspace, targetDPI = 300, opts = {}) {
  const bakePosture = opts.bakePosture === 'singleshot' ? 'singleshot' : 'tiled';
  const { width, height, left, top } = workspace;
  const exportCaps = getRenderCaps();
  const targetMult = targetDPI / 72;
  const targetW = Math.round(width * targetMult);
  const targetH = Math.round(height * targetMult);
  // The 300-DPI need law: need = document inches × targetDPI, delivered
  // exactly. Single surface when the whole raster fits the transient export
  // budgets (24×36@300 = 7200×10800 = 77.8Mpx does); strips beyond.
  const stripMode = targetW > exportCaps.exportSide
    || targetH > exportCaps.exportSide
    || targetW * targetH > exportCaps.exportArea;

  let multiplier = targetMult;
  if (!stripMode) {
    const maxSide = await probeMaxCanvasSide();
    // Conservative total-area cap (some GPUs fail below maxSide² total).
    // With strip mode above this clamp should never bind for real posters —
    // kept as a last-resort safety for exotic devices whose probed budgets
    // disagree with toCanvasElement reality.
    const maxArea = maxSide * maxSide * 0.9;
    multiplier = Math.min(
      multiplier,
      maxSide / Math.max(width, height),
      Math.sqrt(maxArea / (width * height)),
    );
    multiplier = Math.max(multiplier, 0.25);
    if (multiplier < targetMult * 0.98) {
      warnPrintOnce(`export-dpi:${Math.round(width)}x${Math.round(height)}:${targetDPI}`,
        `export clamped to ${Math.round(72 * multiplier)}dpi (target ${targetDPI}): `
        + `${Math.round(width * multiplier)}×${Math.round(height * multiplier)}px vs device max `
        + `side ${maxSide} — unexpected (strip mode should have engaged); report this device.`);
    }
  }

  // Recipe images (text3d, emboss-text): re-bake at print resolution for this
  // render only. restore() runs in the finally below — MANDATORY, or a
  // multi-MB print bake leaks into the next history entry / autosave.
  const restoreBakes = await upscaleRegisteredBakes(canvas, multiplier);
  // Fabric's object-cache ceilings (maxCacheSideLimit 4096, perfLimitSizeTotal
  // ~2.1Mpx²) silently SOFT-render any cached object bigger than that — at a
  // print multiplier every poster headline is. Raise them for this render and
  // restore in the finally; the next screen render re-limits automatically
  // (_limitCacheSize runs on every cache update). fxBeginExport flips the live
  // effects engine to full-quality synchronous rendering (the export raster is
  // the deliverable — no async upgrade will follow it). Objects whose caches
  // exceed even the raised budgets are rendered cache-less in strip mode
  // (disableOversizedCaches) — never silently softened.
  const prevMaxCacheSide = config.maxCacheSideLimit;
  const prevPerfLimitTotal = config.perfLimitSizeTotal;
  config.maxCacheSideLimit = exportCaps.exportSide;
  config.perfLimitSizeTotal = exportCaps.exportArea;
  fxBeginExport();
  // Source-step (live 3D) bakes happen HERE, before the render pass — the
  // render itself never touches WebGL. Cleanup is bound into the finally.
  let fxPrepass = { cleanup: () => {}, ms: 0, primed: 0, singleshotDegraded: 0, bakePosture };
  try {
    fxPrepass = await primeFxSourceBakes(canvas, multiplier, { bakePosture });
  } catch (_) { /* extruded objects export flat */ }
  try {
    // Fonts must be settled or glyphs rasterize with fallbacks
    try { await document.fonts.ready; } catch (_) { /* ignore */ }

    // Poster background texture (workspace data.bgRecipe): its Pattern
    // decodes ASYNC after load/apply — by the flat-JSON law the design JSON
    // never carries it, so unlike object texture fills it gets no free
    // pre-load from loadFromJSON's enliven. A cold export must not race the
    // decode and ship the flat degrade fill: the engine parks the pending
    // resolve on the workspace as _bgPatternPromise (settled, never
    // rejecting) — same posture as fxPrimeAssets' filter-asset warm-up. A
    // dead texture resolves to the flat fallback, which IS the honest
    // deliverable then.
    try { await workspace?._bgPatternPromise; } catch (_) { /* flat fallback exports */ }

    if (stripMode) {
      const t0 = performance.now();
      const res = await renderStripsToPng(canvas, { left, top, width, height }, {
        multiplier,
        targetW,
        targetH,
        targetDPI,
        caps: exportCaps,
        pngFilter: opts.pngFilter,
        pngLevel: opts.pngLevel,
        onProgress: opts.onProgress,
      });
      canvas.requestRenderAll();
      console.info(`[export] strip export ${targetW}×${targetH}@${targetDPI}dpi: `
        + `${res.strips} strips × ${res.stripHeight}px, ${Math.round(res.blob.size / 1048576)}MB `
        + `PNG in ${Math.round(performance.now() - t0)}ms`);
      return {
        blob: res.blob,
        actualDPI: targetDPI,
        widthPx: targetW,
        heightPx: targetH,
        fxPrepassMs: fxPrepass.ms,
        fxPrimed: fxPrepass.primed,
        fxAssetsPrimed: fxPrepass.assetsPrimed,
        fxSingleshotDegraded: fxPrepass.singleshotDegraded,
        bakePosture,
        mode: 'strips',
        strips: res.strips,
        stripHeight: res.stripHeight,
        cols: res.cols,
        renderMs: res.renderMs,
        encodeMs: res.encodeMs,
        peakHeapMB: res.peakHeapMB,
      };
    }

    const prevVpt = canvas.viewportTransform ? [...canvas.viewportTransform] : null;
    canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
    let el;
    try {
      el = canvas.toCanvasElement(multiplier, { left, top, width, height });
    } finally {
      if (prevVpt) canvas.setViewportTransform(prevVpt);
      canvas.requestRenderAll();
    }

    const blob = await new Promise((resolve, reject) => {
      el.toBlob((b) => (b ? resolve(b) : reject(new Error('Export failed (blank canvas?)'))), 'image/png');
    });

    return {
      blob,
      actualDPI: Math.round(72 * multiplier),
      widthPx: Math.round(width * multiplier),
      heightPx: Math.round(height * multiplier),
      fxPrepassMs: fxPrepass.ms, // wall time of the 3D pre-bake pass
      fxPrimed: fxPrepass.primed,
      fxAssetsPrimed: fxPrepass.assetsPrimed,
      fxSingleshotDegraded: fxPrepass.singleshotDegraded,
      bakePosture,
      mode: 'single',
    };
  } finally {
    fxPrepass.cleanup(); // free print-resolution 3D bakes — never outlive the export
    fxEndExport();
    config.maxCacheSideLimit = prevMaxCacheSide;
    config.perfLimitSizeTotal = prevPerfLimitTotal;
    await restoreBakes();
  }
}

// Track the previous print-handoff URL so each new handoff can free the last
// one without yanking it out from under a live PrintPreview.
let lastHandoffUrl = null;
export function createHandoffUrl(blob) {
  if (lastHandoffUrl) {
    try { URL.revokeObjectURL(lastHandoffUrl); } catch (_) { /* ignore */ }
  }
  lastHandoffUrl = URL.createObjectURL(blob);
  return lastHandoffUrl;
}

// ---- remote-asset flattening ----
// Saved designs must be self-contained: Pexels/sticker/legacy-QR images kept
// as remote URLs break offline and can expire. Fetch each remote image and
// embed it as a data URL (per-image size guard; failures keep the remote src).
export async function flattenRemoteImages(canvas, { maxBytes = 8 * 1024 * 1024 } = {}) {
  // Walk nested groups too — since the v6 Group rebuild, real Groups are a
  // routine result of editor.group(), and a remote image inside one must
  // still be embedded (top-level-only walk was a long-standing gap).
  const collect = (objs) => objs.flatMap((o) =>
    typeof o.getObjects === 'function' ? collect(o.getObjects()) : [o]
  );
  const remotes = collect(canvas.getObjects()).filter((o) => {
    if (String(o.type).toLowerCase() !== 'image' || typeof o.getSrc !== 'function') return false;
    const src = o.getSrc() || '';
    return /^https?:/i.test(src);
  });
  let flattened = 0;
  for (const obj of remotes) {
    try {
      const res = await fetch(obj.getSrc());
      if (!res.ok) continue;
      const blob = await res.blob();
      if (blob.size > maxBytes) continue;
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(blob);
      });
      // v6: setSrc is promise-based (the old wrapper's callback was never
      // invoked in v6 — Save hung forever on any design with a remote image).
      await obj.setSrc(dataUrl, { crossOrigin: 'anonymous' });
      flattened += 1;
    } catch (_) { /* keep remote src */ }
  }
  if (flattened > 0) canvas.requestRenderAll();
  return flattened;
}
